/**
 * CEO Daily Briefing Report — Renderer
 *
 * Renders 9 sections: Yesterday's Gross, Units Sold, MTD Totals,
 * Forecast Variance, Open RO Flags, Low FRH Flags, Sales Appointments,
 * Service Appointments, Inventory.
 */

import React, { useState } from 'react';
import { InventoryAgingCard } from '../../forecast/InventoryAgingCard';
import { UsedInventoryAgingCard } from '../../forecast/UsedInventoryAgingCard';
import { InventoryAgingResponse, UsedInventoryAgingResponse } from '../../../services/api';

interface LocationDeptGross {
  locationId: number;
  locationName: string;
  departments: Record<string, number>;
  total: number;
}

interface LocationUnits {
  locationId: number;
  locationName: string;
  newUnits: number;
  usedUnits: number;
  totalUnits: number;
}

interface ForecastVarianceRow {
  locationId: number;
  locationName: string;
  department: string;
  trending: number;
  projectionTarget: number;
  gap: number;
  gapPercent: number;
}

interface OpenROAdvisorRow {
  advisorName: string;
  closedROs: number;
  openROs: number;
  over120: number;
}

interface OpenROLocationRow {
  locationId: number;
  locationName: string;
  closedROs: number;
  openROs: number;
  over120: number;
  advisors: OpenROAdvisorRow[];
}

interface LowFRHFlag {
  techName: string;
  locationId: number;
  locationName: string;
  actualFRH: number;
  expectedFRH: number;
  percentOfExpected: number;
}

interface DayAppointmentCount {
  date: string;
  dayLabel: string;
  scheduled: number;
}

interface AppointmentDetailView {
  time: string;
  reason: string;
  subject?: string;
}

interface SalesPersonDayCount {
  userId: number;
  userName: string;
  days: number[];
  total: number;
  details?: AppointmentDetailView[][];
}

interface LocationAppointments {
  locationId: number;
  locationName: string;
  days: DayAppointmentCount[];
  total: number;
  salesPeople?: SalesPersonDayCount[];
}

interface DayServiceAppointmentCount {
  date: string;
  dayLabel: string;
  booked: number;
  capacity: number;
}

interface LocationServiceAppointments {
  locationId: number;
  locationName: string;
  days: DayServiceAppointmentCount[];
  bookedTotal: number;
  capacityTotal: number;
}

interface LocationInventoryAging {
  locationId: number;
  locationName: string;
  /** New-vehicle snapshot — shape matches `InventoryAgingResponse` so the card consumes it directly */
  new: InventoryAgingResponse | null;
  /** Used-vehicle snapshot — shape matches `UsedInventoryAgingResponse` */
  used: UsedInventoryAgingResponse | null;
}

interface CeoBriefingData {
  reportDate: string;
  asOfWorkingDay: number;
  yesterdayGross: LocationDeptGross[];
  /** Company Total — present only in Consolidated mode (locationId = 0). */
  yesterdayCompanyTotal?: LocationDeptGross;
  unitsSoldYesterday: LocationUnits[];
  unitsCompanyTotal?: LocationUnits;
  mtdGross: LocationDeptGross[];
  mtdCompanyTotal?: LocationDeptGross;
  mtdUnits: LocationUnits[];
  mtdUnitsCompanyTotal?: LocationUnits;
  forecastVariance: ForecastVarianceRow[];
  openROs: OpenROLocationRow[];
  lowFRHFlags: LowFRHFlag[];
  salesAppointments?: LocationAppointments[];
  appointmentsCompanyTotal?: LocationAppointments;
  appointmentsLastUpdated?: string;
  serviceAppointments?: LocationServiceAppointments[];
  serviceAppointmentsCompanyTotal?: LocationServiceAppointments;
  serviceAppointmentsLastUpdated?: string;
  inventoryAging?: LocationInventoryAging[];
}

const DEPT_LABELS: Record<string, string> = {
  NEW_VEHICLE: 'New',
  USED_VEHICLE: 'Used',
  SERVICE: 'Service',
  PARTS: 'Parts',
  BODY_SHOP: 'Body',
  RENTAL: 'Rental',
  OTHERS: 'Others',
};

const DEPT_ORDER = ['NEW_VEHICLE', 'USED_VEHICLE', 'SERVICE', 'PARTS', 'BODY_SHOP', 'RENTAL', 'OTHERS'];

function fmt(value: number): string {
  if (value === 0) return '$0';
  const abs = Math.abs(value);
  const formatted = abs >= 1000
    ? '$' + abs.toLocaleString('en-US', { maximumFractionDigits: 0 })
    : '$' + abs.toFixed(0);
  return value < 0 ? `(${formatted})` : formatted;
}

function fmtPct(value: number): string {
  const sign = value > 0 ? '+' : '';
  return `${sign}${value.toFixed(1)}%`;
}

const LOCATION_ORDER = [1, 2, 3, 4, 5, 10, 11, 12, 14, 44];

/** Short location name for table display */
function shortName(name: string): string {
  return name
    .replace("Darling's ", "")
    .replace("Darlings ", "")
    .replace("Darling's Undercoating & More (Body Shop)", "Undercoating/Body");
}

const SectionHeader: React.FC<{ title: string; subtitle?: string }> = ({ title, subtitle }) => (
  <div className="mb-3 print:break-inside-avoid">
    <h3 className="text-base font-bold text-gray-900">{title}</h3>
    {subtitle && <p className="text-xs text-gray-500">{subtitle}</p>}
  </div>
);

// ─── Section 1: Yesterday's Gross ────────────────────────────────────

const GrossTable: React.FC<{
  rows: LocationDeptGross[];
  companyTotal?: LocationDeptGross;
  title: string;
  subtitle?: string;
}> = ({ rows, companyTotal, title, subtitle }) => (
  <div className="mb-8 print:break-inside-avoid">
    <SectionHeader title={title} subtitle={subtitle} />
    <div className="overflow-x-auto">
      <table className="w-full text-xs border-collapse">
        <thead>
          <tr className="bg-gray-50">
            <th className="text-left px-2 py-1.5 font-semibold text-gray-700 border-b border-gray-200 whitespace-nowrap">Location</th>
            {DEPT_ORDER.map(d => (
              <th key={d} className="text-right px-2 py-1.5 font-semibold text-gray-700 border-b border-gray-200 whitespace-nowrap">
                {DEPT_LABELS[d]}
              </th>
            ))}
            <th className="text-right px-2 py-1.5 font-bold text-gray-900 border-b border-gray-200 whitespace-nowrap">Total</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(row => (
            <tr key={row.locationId} className="hover:bg-gray-50 border-b border-gray-100">
              <td className="px-2 py-1 font-medium text-gray-800 whitespace-nowrap">{shortName(row.locationName)}</td>
              {DEPT_ORDER.map(d => {
                const val = row.departments[d] || 0;
                return (
                  <td
                    key={d}
                    className={`px-2 py-1 text-right tabular-nums ${val < 0 ? 'text-red-600' : 'text-gray-700'}`}
                  >
                    {fmt(val)}
                  </td>
                );
              })}
              <td className={`px-2 py-1 text-right font-semibold tabular-nums ${row.total < 0 ? 'text-red-600' : 'text-gray-900'}`}>
                {fmt(row.total)}
              </td>
            </tr>
          ))}
        </tbody>
        {companyTotal && (
          <tfoot>
            <tr className="bg-gray-100 font-bold border-t-2 border-gray-300">
              <td className="px-2 py-1.5 text-gray-900">Company Total</td>
              {DEPT_ORDER.map(d => {
                const val = companyTotal.departments[d] || 0;
                return (
                  <td key={d} className={`px-2 py-1.5 text-right tabular-nums ${val < 0 ? 'text-red-600' : 'text-gray-900'}`}>
                    {fmt(val)}
                  </td>
                );
              })}
              <td className={`px-2 py-1.5 text-right tabular-nums ${companyTotal.total < 0 ? 'text-red-600' : 'text-gray-900'}`}>
                {fmt(companyTotal.total)}
              </td>
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  </div>
);

// ─── Section 2: Units Sold ───────────────────────────────────────────

const UnitsTable: React.FC<{
  rows: LocationUnits[];
  companyTotal?: LocationUnits;
  title: string;
  subtitle?: string;
}> = ({ rows, companyTotal, title, subtitle }) => (
  <div className="mb-8 print:break-inside-avoid">
    <SectionHeader title={title} subtitle={subtitle} />
    <div className="overflow-x-auto">
      <table className="w-full text-xs border-collapse max-w-lg">
        <thead>
          <tr className="bg-gray-50">
            <th className="text-left px-2 py-1.5 font-semibold text-gray-700 border-b border-gray-200">Location</th>
            <th className="text-right px-2 py-1.5 font-semibold text-blue-700 border-b border-gray-200">New</th>
            <th className="text-right px-2 py-1.5 font-semibold text-green-700 border-b border-gray-200">Used</th>
            <th className="text-right px-2 py-1.5 font-bold text-gray-900 border-b border-gray-200">Total</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(row => (
            <tr key={row.locationId} className="hover:bg-gray-50 border-b border-gray-100">
              <td className="px-2 py-1 font-medium text-gray-800 whitespace-nowrap">{shortName(row.locationName)}</td>
              <td className="px-2 py-1 text-right tabular-nums text-blue-700">{row.newUnits}</td>
              <td className="px-2 py-1 text-right tabular-nums text-green-700">{row.usedUnits}</td>
              <td className="px-2 py-1 text-right font-semibold tabular-nums text-gray-900">{row.totalUnits}</td>
            </tr>
          ))}
        </tbody>
        {companyTotal && (
          <tfoot>
            <tr className="bg-gray-100 font-bold border-t-2 border-gray-300">
              <td className="px-2 py-1.5 text-gray-900">Company Total</td>
              <td className="px-2 py-1.5 text-right tabular-nums text-blue-700">{companyTotal.newUnits}</td>
              <td className="px-2 py-1.5 text-right tabular-nums text-green-700">{companyTotal.usedUnits}</td>
              <td className="px-2 py-1.5 text-right tabular-nums text-gray-900">{companyTotal.totalUnits}</td>
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  </div>
);

// ─── Section 3: MTD Totals ───────────────────────────────────────────

const MTDSection: React.FC<{
  grossRows: LocationDeptGross[];
  grossTotal?: LocationDeptGross;
  unitRows: LocationUnits[];
  unitTotal?: LocationUnits;
  workingDay: number;
}> = ({ grossRows, grossTotal, unitRows, unitTotal, workingDay }) => (
  <div className="mb-8 print:break-inside-avoid">
    <SectionHeader
      title="3. MTD Totals"
      subtitle={`Cumulative through Working Day ${workingDay}`}
    />
    <div className="overflow-x-auto">
      <table className="w-full text-xs border-collapse">
        <thead>
          <tr className="bg-gray-50">
            <th className="text-left px-2 py-1.5 font-semibold text-gray-700 border-b border-gray-200 whitespace-nowrap">Location</th>
            {DEPT_ORDER.map(d => (
              <th key={d} className="text-right px-2 py-1.5 font-semibold text-gray-700 border-b border-gray-200 whitespace-nowrap">
                {DEPT_LABELS[d]}
              </th>
            ))}
            <th className="text-right px-2 py-1.5 font-bold text-gray-900 border-b border-gray-200 whitespace-nowrap">Total</th>
            <th className="text-right px-2 py-1.5 font-semibold text-blue-700 border-b border-gray-200 whitespace-nowrap">New U</th>
            <th className="text-right px-2 py-1.5 font-semibold text-green-700 border-b border-gray-200 whitespace-nowrap">Used U</th>
          </tr>
        </thead>
        <tbody>
          {grossRows.map((row, idx) => {
            const units = unitRows[idx];
            return (
              <tr key={row.locationId} className="hover:bg-gray-50 border-b border-gray-100">
                <td className="px-2 py-1 font-medium text-gray-800 whitespace-nowrap">{shortName(row.locationName)}</td>
                {DEPT_ORDER.map(d => {
                  const val = row.departments[d] || 0;
                  return (
                    <td key={d} className={`px-2 py-1 text-right tabular-nums ${val < 0 ? 'text-red-600' : 'text-gray-700'}`}>
                      {fmt(val)}
                    </td>
                  );
                })}
                <td className={`px-2 py-1 text-right font-semibold tabular-nums ${row.total < 0 ? 'text-red-600' : 'text-gray-900'}`}>
                  {fmt(row.total)}
                </td>
                <td className="px-2 py-1 text-right tabular-nums text-blue-700">{units?.newUnits || 0}</td>
                <td className="px-2 py-1 text-right tabular-nums text-green-700">{units?.usedUnits || 0}</td>
              </tr>
            );
          })}
        </tbody>
        {grossTotal && unitTotal && (
          <tfoot>
            <tr className="bg-gray-100 font-bold border-t-2 border-gray-300">
              <td className="px-2 py-1.5 text-gray-900">Company Total</td>
              {DEPT_ORDER.map(d => {
                const val = grossTotal.departments[d] || 0;
                return (
                  <td key={d} className={`px-2 py-1.5 text-right tabular-nums ${val < 0 ? 'text-red-600' : 'text-gray-900'}`}>
                    {fmt(val)}
                  </td>
                );
              })}
              <td className={`px-2 py-1.5 text-right tabular-nums ${grossTotal.total < 0 ? 'text-red-600' : 'text-gray-900'}`}>
                {fmt(grossTotal.total)}
              </td>
              <td className="px-2 py-1.5 text-right tabular-nums text-blue-700">{unitTotal.newUnits}</td>
              <td className="px-2 py-1.5 text-right tabular-nums text-green-700">{unitTotal.usedUnits}</td>
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  </div>
);

// ─── Section 4: Forecast Variance ────────────────────────────────────

/** Group variance rows by location, compute a location total, and order departments consistently */
function groupVarianceByLocation(rows: ForecastVarianceRow[]): Array<{
  locationId: number;
  locationName: string;
  total: ForecastVarianceRow;
  departments: ForecastVarianceRow[];
}> {
  const byLoc = new Map<number, ForecastVarianceRow[]>();
  for (const row of rows) {
    const existing = byLoc.get(row.locationId) || [];
    existing.push(row);
    byLoc.set(row.locationId, existing);
  }

  return LOCATION_ORDER
    .filter(id => byLoc.has(id))
    .map(id => {
      const deptRows = byLoc.get(id)!;
      const sorted = [...deptRows].sort(
        (a, b) => DEPT_ORDER.indexOf(a.department) - DEPT_ORDER.indexOf(b.department),
      );
      const totalTrending = sorted.reduce((s, r) => s + r.trending, 0);
      const totalTarget = sorted.reduce((s, r) => s + r.projectionTarget, 0);
      const totalGap = totalTrending - totalTarget;
      const totalGapPct = totalTarget !== 0 ? (totalGap / Math.abs(totalTarget)) * 100 : 0;
      return {
        locationId: id,
        locationName: sorted[0].locationName,
        total: {
          locationId: id,
          locationName: sorted[0].locationName,
          department: 'ALL',
          trending: Math.round(totalTrending),
          projectionTarget: Math.round(totalTarget),
          gap: Math.round(totalGap),
          gapPercent: Math.round(totalGapPct * 10) / 10,
        },
        departments: sorted,
      };
    });
}

function groupVarianceByDepartment(rows: ForecastVarianceRow[]): Array<{
  department: string;
  trending: number;
  projectionTarget: number;
  gap: number;
  gapPercent: number;
}> {
  const byDept = new Map<string, { trending: number; projectionTarget: number }>();
  for (const row of rows) {
    const existing = byDept.get(row.department) || { trending: 0, projectionTarget: 0 };
    existing.trending += row.trending;
    existing.projectionTarget += row.projectionTarget;
    byDept.set(row.department, existing);
  }

  return DEPT_ORDER
    .filter(dept => byDept.has(dept))
    .map(dept => {
      const { trending, projectionTarget } = byDept.get(dept)!;
      const gap = trending - projectionTarget;
      const gapPercent = projectionTarget !== 0 ? (gap / Math.abs(projectionTarget)) * 100 : 0;
      return {
        department: dept,
        trending: Math.round(trending),
        projectionTarget: Math.round(projectionTarget),
        gap: Math.round(gap),
        gapPercent: Math.round(gapPercent * 10) / 10,
      };
    });
}

const ForecastVarianceSection: React.FC<{ rows: ForecastVarianceRow[] }> = ({ rows }) => {
  const grouped = groupVarianceByLocation(rows);

  const companyTotal = grouped.reduce(
    (acc, g) => {
      acc.trending += g.total.trending;
      acc.projectionTarget += g.total.projectionTarget;
      return acc;
    },
    { trending: 0, projectionTarget: 0 }
  );
  const companyGap = companyTotal.trending - companyTotal.projectionTarget;
  const companyGapPct = companyTotal.projectionTarget !== 0
    ? (companyGap / Math.abs(companyTotal.projectionTarget)) * 100
    : 0;

  const companyByDept = groupVarianceByDepartment(rows);

  // Location headers collapse to reduce scrolling — all start collapsed
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const toggle = (locationId: number) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(locationId)) next.delete(locationId);
      else next.add(locationId);
      return next;
    });
  };
  const [companyExpanded, setCompanyExpanded] = useState(false);

  return (
    <div className="mb-8 print:break-inside-avoid">
      <SectionHeader
        title="4. Forecast Variance"
        subtitle="Trending vs projection target — grouped by location"
      />
      {rows.length === 0 ? (
        <p className="text-sm text-gray-500 italic">No projection data available.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs border-collapse">
            <thead>
              <tr className="bg-gray-50">
                <th className="text-left px-2 py-1.5 font-semibold text-gray-700 border-b border-gray-200">Location</th>
                <th className="text-left px-2 py-1.5 font-semibold text-gray-700 border-b border-gray-200">Department</th>
                <th className="text-right px-2 py-1.5 font-semibold text-gray-700 border-b border-gray-200">Trending</th>
                <th className="text-right px-2 py-1.5 font-semibold text-gray-700 border-b border-gray-200">Target</th>
                <th className="text-right px-2 py-1.5 font-semibold text-gray-700 border-b border-gray-200">Gap</th>
                <th className="text-right px-2 py-1.5 font-semibold text-gray-700 border-b border-gray-200">Gap %</th>
              </tr>
            </thead>
            <tbody>
              {grouped.map((group) => (
                <React.Fragment key={group.locationId}>
                  {/* Location total row */}
                  <tr
                    className="bg-gray-100 border-t-2 border-gray-300 cursor-pointer hover:bg-gray-200 select-none print:cursor-auto"
                    onClick={() => toggle(group.locationId)}
                  >
                    <td className="px-2 py-1.5 font-bold text-gray-900 whitespace-nowrap">
                      <span className="inline-block w-4 text-gray-600 print:hidden">{expanded.has(group.locationId) ? '▾' : '▸'}</span>
                      {shortName(group.locationName)}
                    </td>
                    <td className="px-2 py-1.5 font-bold text-gray-900">All Depts</td>
                    <td className="px-2 py-1.5 text-right tabular-nums font-bold text-gray-900">{fmt(group.total.trending)}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums font-bold text-gray-900">{fmt(group.total.projectionTarget)}</td>
                    <td className={`px-2 py-1.5 text-right tabular-nums font-bold ${group.total.gap < 0 ? 'text-red-700' : 'text-green-700'}`}>
                      {fmt(group.total.gap)}
                    </td>
                    <td className={`px-2 py-1.5 text-right tabular-nums font-bold ${group.total.gap < 0 ? 'text-red-700' : 'text-green-700'}`}>
                      {fmtPct(group.total.gapPercent)}
                    </td>
                  </tr>
                  {/* Department rows */}
                  {group.departments.map((row, i) => (
                    <tr key={i} className={`border-b border-gray-100 hover:bg-gray-50 print:table-row ${expanded.has(group.locationId) ? '' : 'hidden'}`}>
                      <td className="px-2 py-1 text-gray-400 whitespace-nowrap"></td>
                      <td className="px-2 py-1 text-gray-700 pl-4">{DEPT_LABELS[row.department] || row.department}</td>
                      <td className="px-2 py-1 text-right tabular-nums text-gray-700">{fmt(row.trending)}</td>
                      <td className="px-2 py-1 text-right tabular-nums text-gray-700">{fmt(row.projectionTarget)}</td>
                      <td className={`px-2 py-1 text-right tabular-nums font-semibold ${row.gap < 0 ? 'text-red-700' : 'text-green-700'}`}>
                        {fmt(row.gap)}
                      </td>
                      <td className={`px-2 py-1 text-right tabular-nums font-semibold ${row.gap < 0 ? 'text-red-700' : 'text-green-700'}`}>
                        {fmtPct(row.gapPercent)}
                      </td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
            {grouped.length > 1 && (
              <tfoot>
                <tr
                  className="bg-gray-100 font-bold border-t-2 border-gray-300 cursor-pointer hover:bg-gray-200 select-none print:cursor-auto"
                  onClick={() => setCompanyExpanded(prev => !prev)}
                >
                  <td className="px-2 py-1.5 text-gray-900 whitespace-nowrap">
                    <span className="inline-block w-4 text-gray-600 print:hidden">{companyExpanded ? '▾' : '▸'}</span>
                    Company Total
                  </td>
                  <td className="px-2 py-1.5 text-gray-900">All Depts</td>
                  <td className="px-2 py-1.5 text-right tabular-nums text-gray-900">{fmt(companyTotal.trending)}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums text-gray-900">{fmt(companyTotal.projectionTarget)}</td>
                  <td className={`px-2 py-1.5 text-right tabular-nums ${companyGap < 0 ? 'text-red-700' : 'text-green-700'}`}>
                    {fmt(companyGap)}
                  </td>
                  <td className={`px-2 py-1.5 text-right tabular-nums ${companyGap < 0 ? 'text-red-700' : 'text-green-700'}`}>
                    {fmtPct(companyGapPct)}
                  </td>
                </tr>
                {companyByDept.map((row) => (
                  <tr
                    key={row.department}
                    className={`border-b border-gray-100 hover:bg-gray-50 print:table-row ${companyExpanded ? '' : 'hidden'}`}
                  >
                    <td className="px-2 py-1 text-gray-400 whitespace-nowrap"></td>
                    <td className="px-2 py-1 text-gray-900 font-semibold pl-4">{DEPT_LABELS[row.department] || row.department}</td>
                    <td className="px-2 py-1 text-right tabular-nums font-semibold text-gray-900">{fmt(row.trending)}</td>
                    <td className="px-2 py-1 text-right tabular-nums font-semibold text-gray-900">{fmt(row.projectionTarget)}</td>
                    <td className={`px-2 py-1 text-right tabular-nums font-semibold ${row.gap < 0 ? 'text-red-700' : 'text-green-700'}`}>
                      {fmt(row.gap)}
                    </td>
                    <td className={`px-2 py-1 text-right tabular-nums font-semibold ${row.gap < 0 ? 'text-red-700' : 'text-green-700'}`}>
                      {fmtPct(row.gapPercent)}
                    </td>
                  </tr>
                ))}
              </tfoot>
            )}
          </table>
        </div>
      )}
    </div>
  );
};

// ─── Section 5: Open ROs ─────────────────────────────────────────────

function over120Class(n: number): string {
  if (n >= 5) return 'text-red-700';
  if (n >= 1) return 'text-amber-700';
  return 'text-gray-700';
}

function openClass(n: number): string {
  return n >= 30 ? 'text-amber-800 font-semibold' : 'text-gray-800';
}

const OpenROsSection: React.FC<{ rows: OpenROLocationRow[] }> = ({ rows }) => {
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const toggle = (locationId: number) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(locationId)) next.delete(locationId);
      else next.add(locationId);
      return next;
    });
  };

  const totalClosed = rows.reduce((s, l) => s + l.closedROs, 0);
  const totalOpen = rows.reduce((s, l) => s + l.openROs, 0);
  const totalOver120 = rows.reduce((s, l) => s + l.over120, 0);

  return (
    <div className="mb-8 print:break-inside-avoid">
      <SectionHeader
        title="5. Open ROs"
        subtitle="Click a location to see service advisor breakdown"
      />
      <div className="overflow-x-auto">
        <table className="w-full text-xs border-collapse max-w-2xl">
          <thead>
            <tr className="bg-amber-50">
              <th className="text-left px-2 py-1.5 font-semibold text-amber-800 border-b border-amber-200">Location</th>
              <th className="text-right px-2 py-1.5 font-semibold text-amber-800 border-b border-amber-200">Closed ROs</th>
              <th className="text-right px-2 py-1.5 font-semibold text-amber-800 border-b border-amber-200">Open ROs</th>
              <th className="text-right px-2 py-1.5 font-semibold text-amber-800 border-b border-amber-200">120+ Days</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(loc => {
              const isOpen = expanded.has(loc.locationId);
              return (
                <React.Fragment key={loc.locationId}>
                  <tr
                    className="bg-gray-100 cursor-pointer hover:bg-gray-200 select-none print:cursor-auto"
                    onClick={() => toggle(loc.locationId)}
                  >
                    <td className="px-2 py-1.5 font-bold text-gray-900">
                      <span className="inline-block w-4 text-gray-600 print:hidden">{isOpen ? '▾' : '▸'}</span>
                      {shortName(loc.locationName)}
                      <span className="ml-2 font-normal text-gray-600">({loc.advisors.length})</span>
                    </td>
                    <td className="px-2 py-1.5 text-right tabular-nums font-semibold text-gray-800">{loc.closedROs}</td>
                    <td className={`px-2 py-1.5 text-right tabular-nums ${openClass(loc.openROs)}`}>{loc.openROs}</td>
                    <td className={`px-2 py-1.5 text-right tabular-nums font-semibold ${over120Class(loc.over120)}`}>{loc.over120}</td>
                  </tr>
                  {loc.advisors.map((a, i) => (
                    <tr
                      key={i}
                      className={`border-b border-amber-100 hover:bg-amber-50 print:table-row ${isOpen ? '' : 'hidden'}`}
                    >
                      <td className="px-2 py-1 pl-6 font-medium text-gray-800">{a.advisorName}</td>
                      <td className="px-2 py-1 text-right tabular-nums text-gray-700">{a.closedROs}</td>
                      <td className={`px-2 py-1 text-right tabular-nums ${openClass(a.openROs)}`}>{a.openROs}</td>
                      <td className={`px-2 py-1 text-right tabular-nums font-semibold ${over120Class(a.over120)}`}>{a.over120}</td>
                    </tr>
                  ))}
                </React.Fragment>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="bg-amber-100 border-t-2 border-amber-300">
              <td className="px-2 py-1.5 font-bold text-amber-900">Company Total</td>
              <td className="px-2 py-1.5 text-right tabular-nums font-bold text-amber-900">{totalClosed}</td>
              <td className={`px-2 py-1.5 text-right tabular-nums font-bold ${totalOpen >= 30 ? 'text-amber-900' : 'text-amber-900'}`}>{totalOpen}</td>
              <td className={`px-2 py-1.5 text-right tabular-nums font-bold ${totalOver120 >= 5 ? 'text-red-700' : totalOver120 >= 1 ? 'text-amber-900' : 'text-amber-900'}`}>{totalOver120}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};

// ─── Section 6: Low FRH Flags ────────────────────────────────────────

const LowFRHFlagsSection: React.FC<{ flags: LowFRHFlag[] }> = ({ flags }) => {
  // Group flags by location, sorted by LOCATION_ORDER, techs sorted low→high within each group
  const byLoc = new Map<number, { locationName: string; techs: LowFRHFlag[] }>();
  for (const f of flags) {
    const existing = byLoc.get(f.locationId);
    if (existing) {
      existing.techs.push(f);
    } else {
      byLoc.set(f.locationId, { locationName: f.locationName, techs: [f] });
    }
  }
  const groups = LOCATION_ORDER
    .filter(id => byLoc.has(id))
    .map(id => {
      const g = byLoc.get(id)!;
      g.techs.sort((a, b) => a.actualFRH - b.actualFRH);
      return { locationId: id, ...g };
    });

  // Location headers collapse to reduce scrolling — all start collapsed
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const toggle = (locationId: number) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(locationId)) next.delete(locationId);
      else next.add(locationId);
      return next;
    });
  };

  return (
    <div className="mb-8 print:break-inside-avoid">
      <SectionHeader
        title="6. Low FRH Flags"
        subtitle="Technicians below 80% of expected flat rate hours (8 hrs × completed working days)"
      />
      {flags.length === 0 ? (
        <div className="bg-green-50 border border-green-200 rounded-lg p-3">
          <p className="text-sm text-green-700">No technicians flagged.</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs border-collapse max-w-2xl">
            <thead>
              <tr className="bg-red-50">
                <th className="text-left px-2 py-1.5 font-semibold text-red-800 border-b border-red-200">Technician</th>
                <th className="text-right px-2 py-1.5 font-semibold text-red-800 border-b border-red-200">Actual FRH</th>
              </tr>
            </thead>
            <tbody>
              {groups.map(g => {
                const isOpen = expanded.has(g.locationId);
                return (
                  <React.Fragment key={g.locationId}>
                    <tr
                      className="bg-gray-100 cursor-pointer hover:bg-gray-200 select-none print:cursor-auto"
                      onClick={() => toggle(g.locationId)}
                    >
                      <td colSpan={2} className="px-2 py-1.5 font-bold text-gray-900">
                        <span className="inline-block w-4 text-gray-600 print:hidden">{isOpen ? '▾' : '▸'}</span>
                        {shortName(g.locationName)}
                        <span className="ml-2 font-normal text-gray-600">({g.techs.length})</span>
                      </td>
                    </tr>
                    {g.techs.map((f, i) => (
                      <tr
                        key={i}
                        className={`border-b border-red-100 hover:bg-red-50 print:table-row ${isOpen ? '' : 'hidden'}`}
                      >
                        <td className="px-2 py-1 pl-4 font-medium text-gray-800">{f.techName}</td>
                        <td className="px-2 py-1 text-right tabular-nums text-gray-700">{f.actualFRH.toFixed(1)}</td>
                      </tr>
                    ))}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ─── Section 7: Sales Appointments (5-Day Forward Outlook) ───────────

/** Shorten appointment reason for compact display */
function shortReason(reason: string): string {
  if (reason === 'Sales Appointment') return 'Sales';
  return reason; // "Delivery", "Other" are already short
}

const SalesAppointmentsSection: React.FC<{
  rows: LocationAppointments[];
  companyTotal?: LocationAppointments;
  lastUpdatedAt?: string;
}> = ({ rows, companyTotal, lastUpdatedAt }) => {
  // Headers fall back to the first row's days when there's no company total
  // (single-location mode); we keep both data paths consistent on day count.
  const days = companyTotal?.days || rows[0]?.days || [];

  // Location headers collapse to reduce scrolling — all start collapsed
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const toggle = (locationId: number) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(locationId)) next.delete(locationId);
      else next.add(locationId);
      return next;
    });
  };

  // Salesperson detail expand (keyed by "locationId-userId")
  const [spExpanded, setSpExpanded] = useState<Set<string>>(new Set());
  const toggleSp = (locationId: number, userId: number) => {
    setSpExpanded(prev => {
      const next = new Set(prev);
      const key = `${locationId}-${userId}`;
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // Format lastUpdatedAt as a readable time string
  const formatLastUpdated = (isoString?: string): string => {
    if (!isoString) return '';
    const d = new Date(isoString);
    return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true, timeZone: 'America/New_York' });
  };

  const colCount = days.length + 2; // Location + days + Total

  return (
    <div className="mb-8 print:break-inside-avoid">
      <SectionHeader
        title="7. Sales Appointments"
        subtitle="VinSolutions — scheduled appointments for today + next 4 days, by salesperson"
      />
      {lastUpdatedAt && (
        <p className="text-xs text-gray-500 -mt-2 mb-2 italic">
          Updated from VinSolutions hourly — last updated: {formatLastUpdated(lastUpdatedAt)}
        </p>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-xs border-collapse">
          <thead>
            <tr className="bg-purple-50">
              <th className="text-left px-2 py-1.5 font-semibold text-purple-800 border-b border-purple-200">
                Location
              </th>
              {days.map((day, idx) => (
                <th key={day.date} className={`text-right px-2 py-1.5 font-semibold border-b border-purple-200 whitespace-nowrap ${idx === 0 ? 'text-purple-900' : 'text-purple-700'}`}>
                  {day.dayLabel}
                </th>
              ))}
              <th className="text-right px-2 py-1.5 font-semibold text-purple-900 border-b border-purple-200">
                Total
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map(row => (
              <React.Fragment key={row.locationId}>
                {/* Location header row */}
                <tr
                  className="bg-purple-50/50 border-t border-purple-200 cursor-pointer hover:bg-purple-100 select-none print:cursor-auto"
                  onClick={() => toggle(row.locationId)}
                >
                  <td className="px-2 py-1 font-bold text-gray-900 whitespace-nowrap">
                    <span className="inline-block w-4 text-purple-600 print:hidden">{expanded.has(row.locationId) ? '▾' : '▸'}</span>
                    {shortName(row.locationName)}
                  </td>
                  {row.days.map((day, idx) => (
                    <td key={day.date} className={`px-2 py-1 text-right tabular-nums font-bold ${idx === 0 ? 'text-purple-800' : 'text-gray-900'}`}>
                      {day.scheduled}
                    </td>
                  ))}
                  <td className="px-2 py-1 text-right tabular-nums font-bold text-purple-900">{row.total}</td>
                </tr>
                {/* Per-salesperson rows */}
                {row.salesPeople && row.salesPeople.map(sp => {
                  const hasDetails = sp.details && sp.details.some(d => d.length > 0);
                  const spKey = `${row.locationId}-${sp.userId}`;
                  const isSpExpanded = spExpanded.has(spKey);
                  return (
                    <React.Fragment key={sp.userId}>
                      <tr
                        className={`border-b border-purple-50 print:table-row ${expanded.has(row.locationId) ? '' : 'hidden'} ${hasDetails ? 'cursor-pointer hover:bg-purple-50 select-none' : 'hover:bg-purple-50'}`}
                        onClick={hasDetails ? () => toggleSp(row.locationId, sp.userId) : undefined}
                      >
                        <td className="px-2 py-0.5 pl-5 text-gray-600 whitespace-nowrap">
                          {hasDetails && (
                            <span className="inline-block w-3 text-purple-400 print:hidden">{isSpExpanded ? '▾' : '▸'}</span>
                          )}
                          {sp.userName}
                        </td>
                        {sp.days.map((count, idx) => (
                          <td key={idx} className={`px-2 py-0.5 text-right tabular-nums ${idx === 0 ? 'text-purple-700' : 'text-gray-500'}`}>
                            {count > 0 ? count : ''}
                          </td>
                        ))}
                        <td className="px-2 py-0.5 text-right tabular-nums text-gray-600">{sp.total}</td>
                      </tr>
                      {/* Appointment detail row */}
                      {hasDetails && isSpExpanded && (
                        <tr className={`print:table-row ${expanded.has(row.locationId) ? '' : 'hidden'}`}>
                          <td colSpan={colCount} className="px-2 py-1 pl-8 bg-purple-50/30">
                            <div className="grid" style={{ gridTemplateColumns: `repeat(${days.length}, 1fr)`, gap: '4px' }}>
                              {days.map((day, idx) => {
                                const dayDetails = sp.details?.[idx] || [];
                                return (
                                  <div key={day.date} className="min-w-0">
                                    <div className="text-purple-600 font-semibold mb-0.5" style={{ fontSize: '9px' }}>
                                      {day.dayLabel}
                                    </div>
                                    {dayDetails.length === 0 ? (
                                      <div className="text-gray-300" style={{ fontSize: '9px' }}>—</div>
                                    ) : (
                                      dayDetails.map((appt, ai) => (
                                        <div
                                          key={ai}
                                          className="text-gray-600 truncate"
                                          style={{ fontSize: '10px' }}
                                          title={appt.subject || undefined}
                                        >
                                          {appt.time} <span className="text-purple-500">({shortReason(appt.reason)})</span>
                                        </div>
                                      ))
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </React.Fragment>
            ))}
          </tbody>
          {companyTotal && (
            <tfoot>
              <tr className="bg-purple-100 font-bold border-t-2 border-purple-300">
                <td className="px-2 py-1.5 text-purple-900">{companyTotal.locationName}</td>
                {companyTotal.days.map((day, idx) => (
                  <td key={day.date} className={`px-2 py-1.5 text-right tabular-nums ${idx === 0 ? 'text-purple-900' : 'text-purple-800'}`}>
                    {day.scheduled}
                  </td>
                ))}
                <td className="px-2 py-1.5 text-right tabular-nums text-purple-900">{companyTotal.total}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
};

// ─── Section 8: Service Appointments (5-Day Forward Outlook) ─────────

const ServiceAppointmentsSection: React.FC<{
  rows: LocationServiceAppointments[];
  companyTotal?: LocationServiceAppointments;
  lastUpdatedAt?: string;
}> = ({ rows, companyTotal, lastUpdatedAt }) => {
  const days = companyTotal?.days || rows[0]?.days || [];

  const formatLastUpdated = (isoString?: string): string => {
    if (!isoString) return '';
    const d = new Date(isoString);
    return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true, timeZone: 'America/New_York' });
  };

  const renderCell = (booked: number, capacity: number) =>
    capacity > 0
      ? <><span className="font-bold">{booked}</span><span className="text-gray-400"> / {capacity}</span></>
      : <span className="font-bold">{booked}</span>;

  return (
    <div className="mb-8 print:break-inside-avoid">
      <SectionHeader
        title="8. Service Appointments"
        subtitle="DealerBuilt — booked / max appointments for today + next 4 days"
      />
      {lastUpdatedAt && (
        <p className="text-xs text-gray-500 -mt-2 mb-2 italic">
          Updated nightly from DealerBuilt — last updated: {formatLastUpdated(lastUpdatedAt)}
        </p>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-xs border-collapse">
          <thead>
            <tr className="bg-emerald-50">
              <th className="text-left px-2 py-1.5 font-semibold text-emerald-800 border-b border-emerald-200">
                Location
              </th>
              {days.map((day, idx) => (
                <th key={day.date} className={`text-right px-2 py-1.5 font-semibold border-b border-emerald-200 whitespace-nowrap ${idx === 0 ? 'text-emerald-900' : 'text-emerald-700'}`}>
                  {day.dayLabel}
                </th>
              ))}
              <th className="text-right px-2 py-1.5 font-semibold text-emerald-900 border-b border-emerald-200">
                Total
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map(row => (
              <tr key={row.locationId} className="border-t border-emerald-100 hover:bg-emerald-50/50">
                <td className="px-2 py-1 font-bold text-gray-900 whitespace-nowrap">
                  {shortName(row.locationName)}
                </td>
                {row.days.map((day, idx) => (
                  <td
                    key={day.date}
                    className={`px-2 py-1 text-right tabular-nums ${idx === 0 ? 'text-emerald-800' : 'text-gray-900'}`}
                    title={`${day.date} — Booked: ${day.booked}, Max: ${day.capacity}`}
                  >
                    {renderCell(day.booked, day.capacity)}
                  </td>
                ))}
                <td className="px-2 py-1 text-right tabular-nums text-emerald-900">
                  {renderCell(row.bookedTotal, row.capacityTotal)}
                </td>
              </tr>
            ))}
          </tbody>
          {companyTotal && (
            <tfoot>
              <tr className="bg-emerald-100 font-bold border-t-2 border-emerald-300">
                <td className="px-2 py-1.5 text-emerald-900">{companyTotal.locationName}</td>
                {companyTotal.days.map((day, idx) => (
                  <td
                    key={day.date}
                    className={`px-2 py-1.5 text-right tabular-nums ${idx === 0 ? 'text-emerald-900' : 'text-emerald-800'}`}
                    title={`${day.date} — Booked: ${day.booked}, Max: ${day.capacity}`}
                  >
                    {renderCell(day.booked, day.capacity)}
                  </td>
                ))}
                <td className="px-2 py-1.5 text-right tabular-nums text-emerald-900">
                  {renderCell(companyTotal.bookedTotal, companyTotal.capacityTotal)}
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
};

// ─── Section 9: Inventory ────────────────────────────────────────────

function inventoryPctClass(pct: number): string {
  if (pct >= 30) return 'text-red-600 font-semibold';
  if (pct >= 15) return 'text-amber-600 font-semibold';
  return 'text-gray-900';
}

function computeNewPct(snap: InventoryAgingResponse | null): number | null {
  if (!snap || !snap.available || snap.totalNewUnits === 0) return null;
  return (100 * snap.totalUnits90Plus) / snap.totalNewUnits;
}

const InventorySection: React.FC<{ rows: LocationInventoryAging[] }> = ({ rows }) => {
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const toggle = (locationId: number) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(locationId)) next.delete(locationId);
      else next.add(locationId);
      return next;
    });
  };

  // Company-wide weighted percentages — sum raw numerators and denominators
  // across all locations rather than averaging per-location percentages.
  const companyNewTotals = rows.reduce(
    (acc, r) => {
      if (r.new?.available) {
        acc.total += r.new.totalNewUnits;
        acc.aged += r.new.totalUnits90Plus;
      }
      return acc;
    },
    { total: 0, aged: 0 },
  );
  const companyUsedTotals = rows.reduce(
    (acc, r) => {
      if (r.used?.available) {
        acc.total += r.used.totalUsedUnits;
        acc.aged += r.used.totalUnits60Plus;
      }
      return acc;
    },
    { total: 0, aged: 0 },
  );
  const companyNewPct = companyNewTotals.total > 0
    ? (100 * companyNewTotals.aged) / companyNewTotals.total
    : null;
  const companyUsedPct = companyUsedTotals.total > 0
    ? (100 * companyUsedTotals.aged) / companyUsedTotals.total
    : null;

  return (
    <div className="mb-8 print:break-inside-avoid">
      <SectionHeader
        title="9. Inventory"
        subtitle="Click a location to expand new + used inventory aging detail"
      />
      <div className="overflow-x-auto">
        <table className="w-full text-xs border-collapse max-w-3xl">
          <thead>
            <tr className="bg-gray-50">
              <th className="text-left px-2 py-1.5 font-semibold text-gray-700 border-b border-gray-200 whitespace-nowrap">Location</th>
              <th className="text-right px-2 py-1.5 font-semibold text-gray-700 border-b border-gray-200 whitespace-nowrap">NEW % over 90 Days</th>
              <th className="text-right px-2 py-1.5 font-semibold text-gray-700 border-b border-gray-200 whitespace-nowrap">Used % over 60 Days</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(loc => {
              const isOpen = expanded.has(loc.locationId);
              const newPct = computeNewPct(loc.new);
              const usedPct = loc.used?.available ? loc.used.pct60Plus : null;
              return (
                <React.Fragment key={loc.locationId}>
                  <tr
                    className="bg-gray-100 border-t-2 border-gray-300 cursor-pointer hover:bg-gray-200 select-none print:cursor-auto"
                    onClick={() => toggle(loc.locationId)}
                  >
                    <td className="px-2 py-1.5 font-bold text-gray-900 whitespace-nowrap">
                      <span className="inline-block w-4 text-gray-600 print:hidden">{isOpen ? '▾' : '▸'}</span>
                      {shortName(loc.locationName)}
                    </td>
                    <td className={`px-2 py-1.5 text-right tabular-nums ${newPct !== null ? inventoryPctClass(newPct) : 'text-gray-400'}`}>
                      {newPct !== null ? `${newPct.toFixed(1)}%` : '—'}
                    </td>
                    <td className={`px-2 py-1.5 text-right tabular-nums ${usedPct !== null ? inventoryPctClass(usedPct) : 'text-gray-400'}`}>
                      {usedPct !== null ? `${usedPct.toFixed(1)}%` : '—'}
                    </td>
                  </tr>
                  <tr className={`print:table-row ${isOpen ? '' : 'hidden'}`}>
                    <td colSpan={3} className="px-2 py-3 bg-white">
                      <div className="space-y-3">
                        <InventoryAgingCard
                          data={loc.new}
                          loading={false}
                          error={null}
                          locationName={loc.locationName}
                          collapsed={false}
                        />
                        <UsedInventoryAgingCard
                          data={loc.used}
                          loading={false}
                          error={null}
                          locationName={loc.locationName}
                          collapsed={false}
                        />
                      </div>
                    </td>
                  </tr>
                </React.Fragment>
              );
            })}
          </tbody>
          {rows.length > 1 && (
            <tfoot>
              <tr className="bg-gray-100 font-bold border-t-2 border-gray-300">
                <td className="px-2 py-1.5 text-gray-900 whitespace-nowrap">Company Total</td>
                <td
                  className={`px-2 py-1.5 text-right tabular-nums ${companyNewPct !== null ? inventoryPctClass(companyNewPct) : 'text-gray-400'}`}
                  title={companyNewPct !== null ? `${companyNewTotals.aged.toLocaleString()} of ${companyNewTotals.total.toLocaleString()} new units over 90 days` : undefined}
                >
                  {companyNewPct !== null ? `${companyNewPct.toFixed(1)}%` : '—'}
                </td>
                <td
                  className={`px-2 py-1.5 text-right tabular-nums ${companyUsedPct !== null ? inventoryPctClass(companyUsedPct) : 'text-gray-400'}`}
                  title={companyUsedPct !== null ? `${companyUsedTotals.aged.toLocaleString()} of ${companyUsedTotals.total.toLocaleString()} used units over 60 days` : undefined}
                >
                  {companyUsedPct !== null ? `${companyUsedPct.toFixed(1)}%` : '—'}
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
};

// ─── Main Renderer ───────────────────────────────────────────────────

export const CeoDailyBriefingReport: React.FC<{ data: any }> = ({ data }) => {
  const d = data as CeoBriefingData;

  if (!d || !d.yesterdayGross) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <p className="text-yellow-700">Report data is incomplete. Please try running the report again.</p>
      </div>
    );
  }

  return (
    <div className="space-y-2 print:space-y-4" style={{ fontSize: '12px' }}>
      {/* Print orientation hint */}
      <style>{`@media print { @page { size: landscape; margin: 0.5in; } }`}</style>

      <div className="mb-4">
        <p className="text-sm text-gray-600">
          Report Date: <span className="font-semibold">{d.reportDate}</span>
          {d.asOfWorkingDay > 0 && (
            <span className="ml-3">Working Day: <span className="font-semibold">{d.asOfWorkingDay}</span></span>
          )}
        </p>
      </div>

      {/* Section 1 */}
      <GrossTable
        rows={d.yesterdayGross}
        companyTotal={d.yesterdayCompanyTotal}
        title="1. Last Working Day Gross"
        subtitle={`Gross added on Working Day ${d.asOfWorkingDay} (change from WD ${d.asOfWorkingDay > 1 ? d.asOfWorkingDay - 1 : 0})`}
      />

      {/* Section 2 */}
      <UnitsTable
        rows={d.unitsSoldYesterday}
        companyTotal={d.unitsCompanyTotal}
        title="2. Units Sold Yesterday"
        subtitle="Retail units only (no swaps/wholesale)"
      />

      {/* Section 3 */}
      <MTDSection
        grossRows={d.mtdGross}
        grossTotal={d.mtdCompanyTotal}
        unitRows={d.mtdUnits}
        unitTotal={d.mtdUnitsCompanyTotal}
        workingDay={d.asOfWorkingDay}
      />

      {/* Section 4 */}
      <ForecastVarianceSection rows={d.forecastVariance} />

      {/* Section 5 */}
      <OpenROsSection rows={d.openROs} />

      {/* Section 6 */}
      <LowFRHFlagsSection flags={d.lowFRHFlags} />

      {/* Section 7 — only shown when appointment data exists.
          companyTotal is omitted in single-location mode; the section
          renders without a Company Total tfoot row in that case. */}
      {d.salesAppointments && d.salesAppointments.length > 0 && (
        <SalesAppointmentsSection
          rows={d.salesAppointments}
          companyTotal={d.appointmentsCompanyTotal}
          lastUpdatedAt={d.appointmentsLastUpdated}
        />
      )}

      {/* Section 8 — only shown when service-appointment data exists */}
      {d.serviceAppointments && d.serviceAppointments.length > 0 && (
        <ServiceAppointmentsSection
          rows={d.serviceAppointments}
          companyTotal={d.serviceAppointmentsCompanyTotal}
          lastUpdatedAt={d.serviceAppointmentsLastUpdated}
        />
      )}

      {/* Section 9 — only shown when inventory aging data exists */}
      {d.inventoryAging && d.inventoryAging.length > 0 && (
        <InventorySection rows={d.inventoryAging} />
      )}
    </div>
  );
};
